import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Chip,
  Button,
  CircularProgress,
  TextField,
} from '@material-ui/core';

import 'translations/translations';

import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  addContainer: {
    display: 'flex',
  },
  textField: {
    flex: 1,
    minWidth: '150px',
  },
  button: {
    marginLeft: theme.spacing(2),
    display: 'flex',
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  chips: {
    marginTop: theme.spacing(2),
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
}));

type MailingListProps = {
  emails: string[];
  onDelete?: (email: string) => void;
  onAdd?: (email: string) => void;
};

const emailRegExp =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const MailingList: React.FC<MailingListProps> = (props) => {
  const { t } = useTranslation(['translations']);
  const classes = useStyles();

  const { emails, onDelete, onAdd } = props;

  const [emailToAdd, setEmailToAdd] = useState<string>('');
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [emailsAdding, setEmailsAdding] = useState<string[]>([]);
  const [emailsDeleting, setEmailsDeleting] = useState<string[]>([]);

  const handleEmailChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmailToAdd(value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleAddClicked();
  };

  const handleDelete = (email: string) => {
    if (onDelete) {
      onDelete(email);
    }

    setEmailsDeleting((prevItems) => {
      if (prevItems.includes(email)) {
        return prevItems;
      }

      return [...prevItems, email];
    });
  };

  const handleAddClicked = () => {
    if (!emailValid) {
      return;
    }

    if (onAdd) {
      onAdd(emailToAdd);
    }

    setEmailsAdding((prevItems) => {
      if (prevItems.includes(emailToAdd)) {
        return prevItems;
      }

      return [...prevItems, emailToAdd];
    });

    setEmailToAdd('');
  };

  useEffect(() => {
    setEmailValid(emailRegExp.test(emailToAdd));
  }, [emailToAdd]);

  useEffect(() => {
    setEmailsAdding((prevItems) => {
      return prevItems.filter((item) => !emails.includes(item));
    });
    setEmailsDeleting((prevItems) => {
      return prevItems.filter((item) => emails.includes(item));
    });
  }, [emails]);

  return (
    <div>
      <form noValidate onSubmit={handleSubmit}>
        <div className={classes.addContainer}>
          <div className={classes.textField}>
            <TextField
              type="email"
              variant="outlined"
              fullWidth
              label={t('common.email')}
              value={emailToAdd}
              onChange={handleEmailChange}
              error={emailToAdd !== '' && !emailValid}
            />
          </div>
          <div className={classes.button}>
            <Button
              variant="contained"
              color="secondary"
              component="label"
              onClick={handleAddClicked}
              fullWidth
              disabled={!emailValid}
            >
              <span className={classes.plusIcon}>+</span>
              {t('common.add')}
            </Button>
          </div>
        </div>
      </form>
      {emails.length === 0 && emailsAdding.length === 0 && (
        <p>
          {t(
            'department_page.department_information.mailing_list.no_recipients',
          )}
        </p>
      )}
      <div className={classes.chips}>
        {emails?.map((email: string, index: number) => {
          const isDeleting = emailsDeleting.includes(email);
          return (
            <Chip
              key={index}
              disabled={isDeleting}
              label={email}
              onDelete={() => handleDelete(email)}
              deleteIcon={
                isDeleting ? <CircularProgress size="22" /> : undefined
              }
            />
          );
        })}
        {emailsAdding?.map((email: string, index: number) => {
          return (
            <Chip
              key={index}
              disabled={true}
              label={email}
              onDelete={() => {
                // We need to add this method to activate the delete icon
              }}
              deleteIcon={<CircularProgress size="22" />}
            />
          );
        })}
      </div>
    </div>
  );
};

export { MailingList };
